'use client';

import React from 'react';
import { getAiSearchSuggestions, type AiSearchSuggestion } from '@repo/api';

// Starter prompts are identical for every caller and safe to cache. Fetch once
// per session and reuse across mounts (desktop + mobile bars).
let cached: AiSearchSuggestion[] | null = null;
let inflight: Promise<AiSearchSuggestion[]> | null = null;

async function fetchSuggestions(): Promise<AiSearchSuggestion[]> {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = getAiSearchSuggestions()
    .then(res => {
      cached = res.suggestions ?? [];
      return cached;
    })
    .catch(() => {
      cached = [];
      return cached;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/**
 * The starter prompts shown under an empty AI search bar. Only fetches when
 * `enabled` (i.e. the AI bar is actually rendered).
 */
export function useAiSearchSuggestions(enabled: boolean): AiSearchSuggestion[] {
  const [suggestions, setSuggestions] = React.useState<AiSearchSuggestion[]>(() => cached ?? []);

  React.useEffect(() => {
    if (!enabled) return;
    let active = true;
    void fetchSuggestions().then(s => {
      if (active) setSuggestions(s);
    });
    return () => {
      active = false;
    };
  }, [enabled]);

  return suggestions;
}

export default useAiSearchSuggestions;
